import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  Image,
  Checkbox,
  Button,
  Input,
  Empty,
  Spin,
  message,
  Space,
  Tag,
  Typography,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  FileImageOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const { Search } = Input;
const { Text } = Typography;

interface MediaFile {
  uid: string;
  name: string;
  size: number;
  uploadDate: string;
  url: string;
  type: string;
}

interface FileBrowserWizardProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (selectedFiles: MediaFile[]) => void;
  title?: string;
  multiple?: boolean;
  maxSelection?: number;
}

export const FileBrowserWizard: React.FC<FileBrowserWizardProps> = ({
  visible,
  onClose,
  onSelect,
  title = 'Medien auswählen',
  multiple = true,
  maxSelection = 10,
}) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<MediaFile[]>([]);

  const API_BASE_URL = '/rest/api';

  // Media-Liste laden
  const loadMediaFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=listUserMedia`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
      } else {
        message.error('Fehler beim Laden der Medien');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Medien:', error);
      message.error('Fehler beim Laden der Medien');
    } finally {
      setLoading(false);
    }
  };

  // Datei-Auswahl umschalten
  const toggleFileSelection = (file: MediaFile) => {
    if (!multiple) {
      // Single-Selection: Nur eine Datei auswählen
      setSelectedFiles([file]);
      return;
    }

    // Multi-Selection
    const isSelected = selectedFiles.some(f => f.uid === file.uid);
    
    if (isSelected) {
      // Datei deselektieren
      setSelectedFiles(selectedFiles.filter(f => f.uid !== file.uid));
    } else {
      // Datei auswählen (mit Limit-Prüfung)
      if (selectedFiles.length >= maxSelection) {
        message.warning(`Maximal ${maxSelection} Dateien können ausgewählt werden`);
        return;
      }
      setSelectedFiles([...selectedFiles, file]);
    }
  };

  // Alle Dateien deselektieren
  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Auswahl bestätigen
  const handleConfirm = () => {
    if (selectedFiles.length === 0) {
      message.warning('Bitte wählen Sie mindestens eine Datei aus');
      return;
    }
    
    onSelect(selectedFiles);
    setSelectedFiles([]);
    onClose();
  };

  // Modal schließen
  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  // Gefilterte Dateien basierend auf Suchterm
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Dateigröße formatieren
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Prüfe ob Datei ausgewählt ist
  const isFileSelected = (fileUid: string) => {
    return selectedFiles.some(f => f.uid === fileUid);
  };

  // Komponente initialisieren wenn Modal geöffnet wird
  useEffect(() => {
    if (visible) {
      loadMediaFiles();
    }
  }, [visible]);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleClose}
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button key="clear" onClick={clearSelection} disabled={selectedFiles.length === 0}>
          Auswahl löschen
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          Abbrechen
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          disabled={selectedFiles.length === 0}
          icon={<CheckOutlined />}
        >
          {selectedFiles.length > 0 
            ? `${selectedFiles.length} ${selectedFiles.length === 1 ? 'Datei' : 'Dateien'} auswählen`
            : 'Auswählen'
          }
        </Button>,
      ]}
    >
      {/* Suchbereich */}
      <div style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={12}>
            <Search
              placeholder="Dateiname suchen..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space>
              <Tag color="blue">{filteredFiles.length} verfügbare Dateien</Tag>
              {selectedFiles.length > 0 && (
                <Tag color="green">{selectedFiles.length} ausgewählt</Tag>
              )}
              {multiple && (
                <Text type="secondary">
                  Max. {maxSelection} Dateien
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Datei-Galerie */}
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Medien werden geladen...</div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <Empty
            description={
              searchTerm
                ? `Keine Medien gefunden für "${searchTerm}"`
                : 'Noch keine Medien vorhanden'
            }
            image={<FileImageOutlined style={{ fontSize: '64px', color: '#ccc' }} />}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {filteredFiles.map((file) => {
              const isSelected = isFileSelected(file.uid);
              
              return (
                <Col key={file.uid} xs={24} sm={12} md={8} lg={6} xl={4}>
                  <Card
                    hoverable
                    style={{
                      border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#f0f8ff' : 'white',
                    }}
                    cover={
                      <div 
                        style={{ 
                          height: '120px', 
                          overflow: 'hidden',
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleFileSelection(file)}
                      >
                        <Image
                          src={file.url}
                          alt={file.name}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }}
                          preview={false}
                        />
                        {isSelected && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              backgroundColor: '#1890ff',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                            }}
                          >
                            <CheckOutlined style={{ fontSize: '12px' }} />
                          </div>
                        )}
                      </div>
                    }
                    actions={[
                      <Checkbox
                        key="select"
                        checked={isSelected}
                        onChange={() => toggleFileSelection(file)}
                      >
                        Auswählen
                      </Checkbox>
                    ]}
                    onClick={() => toggleFileSelection(file)}
                  >
                    <Card.Meta
                      title={
                        <Tooltip title={file.name}>
                          <div style={{ 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            fontSize: '12px'
                          }}>
                            {file.name}
                          </div>
                        </Tooltip>
                      }
                      description={
                        <div style={{ fontSize: '11px' }}>
                          <div>{formatFileSize(file.size)}</div>
                          <div style={{ color: '#999' }}>
                            {new Date(file.uploadDate).toLocaleDateString('de-DE')}
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* Ausgewählte Dateien Vorschau */}
      {selectedFiles.length > 0 && (
        <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <Text strong>Ausgewählte Dateien ({selectedFiles.length}):</Text>
          <div style={{ marginTop: '8px' }}>
            <Row gutter={[8, 8]}>
              {selectedFiles.map((file) => (
                <Col key={file.uid}>
                  <Tag 
                    closable 
                    onClose={() => toggleFileSelection(file)}
                    style={{ margin: '2px' }}
                  >
                    {file.name}
                  </Tag>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      )}
    </Modal>
  );
}; 